<template>
    <div class="company">
        <div v-if="shouldShowDetails" class="details">
            <company-details />
        </div>

        <div v-else id="create-view-edit-delete">
            <index
                :payload-for-index="payloadForIndex"
                @view="view"
                @edit="edit"
                @remove="remove"
                @go-to-details="goToDetails"
                @showMigrateCompanyModal="showMigrateCompanyModal"
            />

            <view-or-edit
                ref="createOrEdit"
                v-model="isCompanySidebarActive"
                :data-status="status"
                :data-company="company"
                :data-sidebar-action-loading="isSideBarActionLoading"
                :data-company-status="companyStatus"
                @update-company="update"
            />

            <!-- delete -->
            <confirmation-modal  ref="modal">
                <template slot="body">
                    <div>
                        <feather-icon class="d-block mx-auto text-warning" icon="AlertCircleIcon" size="90"/>
                        <h1 class="text-center text-secondary display-5 mt-2">Are you sure?</h1>
                        <p class="text-center mt-2"> You won't be able to revert this! </p>
                    </div>
                </template>

                <template slot="footer">
                    <b-button class="d-flex" :disabled="isSideBarActionLoading" size="md" variant="primary" @click="confirmDelete">
                        Yes, delete it! 
                        <normal-loader v-if="isSideBarActionLoading" class="left-10%" />
                    </b-button>

                    <b-button size="md" variant="outline-danger" @click="cancelDelete">
                        Cancel
                    </b-button>
                </template>
            </confirmation-modal>
        </div>

        <div class="migration">
            <migrate-existing-companies-modal 
                ref="migrateExistingCompanies"
                :is-processing-migration="isProcessingMigration"
                @migrateExistingCompany="migrateExistingCompany"
            />
        </div>

    </div>
</template>

<script>
import {
    BButton,
} from "bootstrap-vue";
import { mapGetters } from "vuex";
import { ref } from '@vue/composition-api';
import { errorToast, successToast } from '@/views/helpers/toast';
import ConfirmationModal from '@/views/components/utils/ConfirmationModal.vue';
import { BACKEND_VALIDATION_STATUS_CODE, SOMETHING_WENT_WRONG } from '@/constants/errors';
import { COMPANY_DELETE_SUCCESS, COMPANY_UPDATE_SUCCESS } from '@/constants/success';
import Company from '@/models/Company';
import { updateCurrentCompanyInStore } from '@/helpers';
import store from '@/store';
import useCompaniesTable from './useCompaniesTable';
import viewOrEdit from './ViewOrEdit.vue';
import CompanyDetails from './CompanyDetails.vue';
import Index from './Index.vue';
import MigrateExistingCompaniesModal from './MigrateExistingCompanyModal.vue';

export default {
    components: {
        Index,
        BButton,
        viewOrEdit,
        ConfirmationModal,
        CompanyDetails,

        MigrateExistingCompaniesModal,
    },

    data() {
        return {
            status: undefined,
            company: {},
            shouldShowDetails: false,
            isCompanySidebarActive: false,
            companyStatus: [],
            isSideBarActionLoading: false,
            isProcessingMigration: false,
            interValIds: [],
            liveSecretaryFolderPaths: []
        };
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),
    },

    watch: {
        '$route.query': {
            handler(query) {
                if(! query.view){
                    this.shouldShowDetails = false;
                }
            },
        },
    },

    mounted()
    {
        // just to make sure the menu bar is shown
        this.$store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false);
    },

    async created() {
        const companyStatusData = await Company.getStatus();
        
        this.companyStatus = companyStatusData.map(status => ({
            text: status,
            value: status
        }));
    },

    // beforeDestroy () {
    //     this.interValIds.forEach(live => {
    //         clearInterval(live.intervalId);
    //     });
    // },

    methods:{
        view(data) {
            this.status = "view";
            this.company = data.item;
            this.isCompanySidebarActive = true;
        },

        edit(data) {
            this.company = data.item;
            this.status = "edit";
            this.isCompanySidebarActive = true;
        },

        goToDetails() {
            this.shouldShowDetails = true;
            this.$router.push({ path: 'companies', query: { view: 'visualize' } });
        },

        // migrate company region
        showMigrateCompanyModal() {
            this.$refs.migrateExistingCompanies.$refs.modal.show();
        },

        migrateExistingCompany(company) {
            this.isProcessingMigration = true;
            const {companyName} = company;

            Company.migrate(company)
                .then( response => {
                    this.$refs.migrateExistingCompanies.$refs.modal.hide();
                    this.$refs.migrateExistingCompanies.clearForm();
                    this.isProcessingMigration = false;
                    successToast(`Migrating ${companyName}.`);

                    this.liveSecretaryFolderPaths.push(response.data);
                    this.$cache.set('migratingSecretaryFolderPaths', this.liveSecretaryFolderPaths);

                    this.setMigrationLiveStatus(this.liveSecretaryFolderPaths.slice(-1)[0]);
                })
                .catch(error => {
                    this.isProcessingMigration = false;
                    console.log("Error while trying to migrate company", error);

                    if (error.response.status === BACKEND_VALIDATION_STATUS_CODE) {
                        const e = error.response.data.error;
                        errorToast(Object.keys(e).map(key => e[key]).join(", "));
                        return;
                    }

                    try {
                        JSON.parse(error.response.data.message);
                        errorToast(`Invalid secretary folder id for ${company.companyName} Company`);
                    } catch {
                        errorToast(error.response.data.message);
                    }
                });
        },

        setMigrationLiveStatus(companyFolderPath) {
            if(companyFolderPath) {
                const intervalId = setInterval(async() => {
                    Company.getMigrationStatus(companyFolderPath)
                        .then(response => {
                            if(response.status !== 'processing') {
                                if(response.status === 'success') {
                                    this.companies.push(this.localizeData(response.company));
                                    // add to company switch list
                                    this.$store.dispatch('company/refreshCompanies');
                                    // const allCompanies = this.$store.getters['company/getAllUserCompanies'];
                                    // allCompanies.push(response.company);
                                    // this.$store.commit('company/UPDATE_USER_COMPANIES', allCompanies);

                                    successToast(`${response?.company.name} has been migrated successfully.`);
                                }
                                else errorToast(`${response?.company_name} migration error occur. Please try again later.`);

                                this.removeFromLive(companyFolderPath);
                                clearInterval(intervalId);
                            }
                        })
                        .catch(e => {
                            clearInterval(intervalId);
                            console.log("Error while trying to migrate", e);
                        });
                }, 9000);

                this.interValIds.push({'companyFolderPath': companyFolderPath, 'intervalId': intervalId});
            }
        },

        removeFromLive(companyFolderPath) {
            this.liveSecretaryFolderPaths = this.liveSecretaryFolderPaths.filter(liveId => liveId.companyFolderPath !== companyFolderPath.companyFolderPath);
            this.$cache.set('migratingSecretaryFolderPaths', this.liveSecretaryFolderPaths);
        },
        // end region
    },

    setup() {
        const filterMode = ref(false);
        const dataToDelete = ref(undefined);

        const {
            // table data
            sortBy,
            perPage,
            isBusy,
            dataMeta,
            companies,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            localizeData,
            totalCompanies,
            perPageOptions,
            refCompanyListTable,

            fetchCompanies,
            refetchData,

            // UI
            resolveCompanyStatusVariant,
            isLoading,
        } = useCompaniesTable();

        const payloadForIndex = {
            refetchData,
            sortBy,
            perPage,
            isBusy,
            dataMeta,
            companies,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            isLoading,
            perPageOptions,
            fetchCompanies,
            totalCompanies,
            refCompanyListTable,
            resolveCompanyStatusVariant,
        };

        function update(data) {
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;

                Company.update(data.id, data)
                    .then(() => {
                        // refresh the data in vuex
                        const companyId = store.getters['company/getCompanyId']; 
                        
                        if (companyId === data.id) updateCurrentCompanyInStore();

                        this.isCompanySidebarActive = false;
                        this.isSideBarActionLoading = false;

                        refCompanyListTable.value.localItems.forEach(item => {
                            if(item.id === data.id) {
                                Object.keys(data).forEach(key => {
                                    // eslint-disable-next-line no-param-reassign
                                    item[key] = data[key];
                                });
                            }
                        });
                        this.company = {};

                        successToast(COMPANY_UPDATE_SUCCESS);
                    })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to update company",
                            e
                        );
                    });
            }
        };

        function remove(data) {
            this.$refs.modal.$refs.confirmationModal.show();
            dataToDelete.value= data;
        };

        function cancelDelete() {
            dataToDelete.value = null;
            this.$refs.modal.$refs.confirmationModal.hide();
        }

        function confirmDelete() {
            if (this.isUserAdmin) {
                this.isSideBarActionLoading = true;

                Company.delete(dataToDelete.value.item.id)
                    .then(() => {
                        const companyId = store.getters['company/getCompanyId']; 

                        if (companyId === dataToDelete.value.item.id)
                            updateCurrentCompanyInStore();

                        refCompanyListTable.value.localItems = refCompanyListTable.value.localItems.filter(item => dataToDelete.value.item.id !== item.id);
                        this.$refs.modal.$refs.confirmationModal.hide();

                        this.isSideBarActionLoading = false;
                        dataToDelete.value = null;
                        this.$store.dispatch('company/refreshCompanies').catch(() => {});
                        successToast(COMPANY_DELETE_SUCCESS);
                    })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to delete company",
                            e
                        );
                    });
            }
        };

        return {
            refCompanyListTable,
            refetchData,

            // UI
            filterMode,

            // methods
            update,
            remove,
            confirmDelete,
            cancelDelete,
            payloadForIndex,
            localizeData,
            companies,
        };
    }
};
</script>
